"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Compass, Languages, Building2, TrendingUp, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const strategicPillars = [{
  icon: MapPin,
  title: "Micro-District Geo-Targeting",
  tagline: "Hyper-local focus across Dubai's distinct hubs",
  description: "Search behaviour in Dubai is intensely district-driven. I structure your on-page entities, localized schemas, and landing pages to dominate specific high-value zones—from Downtown Dubai, DIFC, and Business Bay to Dubai Marina, JLT, and Palm Jumeirah."
}, {
  icon: Languages,
  title: "Bilingual Search Nuance",
  tagline: "English and Arabic intent alignment",
  description: "Dubai's demographic split requires distinct linguistic architecture. Bilingual English/Arabic capability is evaluated per client rather than applied as a blanket default — I assess your target audience and competitor language use to determine the best localization strategy for your business."
}, {
  icon: Building2,
  title: "Google Business Profile Authority",
  tagline: "Dominating the Dubai 3-Pack & Maps",
  description: "Map rankings drive the majority of high-intent phone calls and store visits in the UAE. Google's local-pack ranking depends on Google Business Profile completeness, review volume and quality, website relevance to local queries, and proximity to the searcher. I personally audit, optimize, and fortify your Google Business Profile attributes, geo-relevance signals, and local review velocity."
}, {
  icon: ShieldCheck,
  title: "UAE Regional Entity & Link Trust",
  tagline: "Authoritative local citations and references",
  description: "I secure contextual placements and verifiable citations across recognized UAE registries, commercial directories, and established regional publications to establish unquestioned local authority in Google's Knowledge Graph."
}];
const executionSteps = [{
  step: "01",
  title: "Deep Dubai Market & Competitor Audit",
  detail: "I analyze existing rankings, localized search intent, and the exact local competitor footprint in your Dubai target districts."
}, {
  step: "02",
  title: "Entity & Schema Architecture",
  detail: "I implement JSON-LD LocalBusiness schemas, geo-coordinates, ServiceArea definitions, and multi-location URL hierarchies."
}, {
  step: "03",
  title: "Localized Content & Citation Building",
  detail: "I create location-specific landing pages and establish consistent Name, Address, and Phone (NAP) citations across UAE portals."
}, {
  step: "04",
  title: "Continuous Map Pack & Conversion Monitoring",
  detail: "I track grid-based local rankings, call conversions, direction requests, and organic revenue directly with you every month."
}];
export default function DubaiStrategy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="dubailocalseostrategy" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 overflow-hidden font-default">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Header Section */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-medium bg-secondary text-secondary-foreground border border-border">
              <Compass className="size-3.5 text-primary mr-1" />
              Regional Market Strategy
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary tracking-tight">
            My Local SEO Strategy for the Dubai Market
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Dubai is one of the most competitive search environments in the world. As an independent SEO consultant, I devise tailored local strategies that help businesses capture high-intent inquiries from residents, corporate buyers, and high-net-worth clients.
          </motion.p>
        </div>

        {/* 4 Strategic Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {strategicPillars.map((pillar, index) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div className="flex items-center justify-center size-11 rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Pillar 0{index + 1}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-bold text-primary">
                      {pillar.title}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-foreground/80">
                      {pillar.tagline}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* First-Person Direct Value Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="rounded-2xl border border-border bg-muted/40 p-8 sm:p-10 mb-16 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 text-primary font-semibold text-xs tracking-wide uppercase mb-3">
                <Sparkles className="size-4 text-accent" />
                Direct Consultant Execution
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-primary mb-4">
                You work directly with me — no account managers or outsourced junior teams
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                When you partner with me on your Dubai search visibility, every technical audit, schema deployment, content specification, and local link strategy is executed by me personally. You get direct access, prompt communication, and clear deliverables tailored to your business objectives.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm font-medium text-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Custom roadmap for your specific industry</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>100% transparent reporting and analytics</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Direct phone, email, and video access</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>No long-term lock-in contracts</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 flex flex-col items-start lg:items-end justify-center gap-4">
              <div className="w-full bg-card p-6 rounded-xl border border-border text-left shadow-xs">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-1">
                  <TrendingUp className="size-4 text-accent" />
                  <span>Targeted Outcomes</span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Focused on sustainable Google 3-Pack placement and high-intent customer acquisition across the UAE.
                </p>
                <Separator className="my-3" />
                <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm font-medium">
                  <Link to="/LocalSEOPage">
                    Explore Local SEO Services
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Process Roadmap */}
        <div className="mb-16">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-primary">
              How I Execute Your Dubai SEO Campaign
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              A structured, step-by-step methodology built for measurable local performance.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {executionSteps.map((item, index) => <div key={item.step} data-index={index} className="relative p-6 rounded-xl bg-card border border-border shadow-xs flex flex-col justify-between">
                <div>
                  <span className="text-3xl font-bold text-primary/20 block mb-3">
                    {item.step}
                  </span>
                  <h4 className="font-semibold text-base text-foreground mb-2">
                    {item.title}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Final Conversion Action */}
        <div className="text-center bg-card border border-border rounded-2xl p-8 sm:p-12 shadow-sm">
          <h3 className="text-2xl sm:text-3xl font-bold text-primary mb-3">
            Ready to expand your search footprint in Dubai?
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-8">
            Let us review your current local search presence, identify district opportunities, and structure a bespoke plan for your business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm px-6 font-medium">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-border text-foreground hover:bg-secondary">
              <Link to="/LocalSEOPage">
                View All Local SEO Details
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}